// src/pages/ProductDetail.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { doc, getDoc, collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import styled, { createGlobalStyle, keyframes, css } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

/* ── Global ─────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Figtree:wght@400;500;600&display=swap');
  /* *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; } */
  /* body { background: #f7f8fc; } */
  /* ::selection { background: #1a56ff22; } */
`;

const C = {
  bg: "#f7f8fc",
  white: "#ffffff",
  blue: "#1a56ff",
  blueSoft: "#eef2ff",
  blueDeep: "#0f3acc",
  text: "#0d0d0d",
  sub: "#6b7280",
  border: "#e8eaed",
  surface: "#f0f1f5",
};

/* ── Shimmer ────────────────────────────────────────────────── */
const shimmer = keyframes`
  0%   { background-position: -700px 0; }
  100% { background-position:  700px 0; }
`;
const skeletonAnim = css`
  background: linear-gradient(90deg, #ebebeb 25%, #f5f5f5 50%, #ebebeb 75%);
  background-size: 700px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

/* ── Page shell ─────────────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  background: ${C.bg};
  font-family: "Montserrat", sans-serif;
  margin-top: 7em;
`;

const Wrap = styled.div`
  max-width: 1360px;
  margin: 0 auto;
  padding: 0 48px;
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

/* ── Breadcrumb ─────────────────────────────────────────────── */
const Breadcrumb = styled.nav`
  padding: 28px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${C.sub};

  a {
    color: ${C.sub};
    text-decoration: none;
    transition: color 0.15s;
    &:hover {
      color: ${C.blue};
    }
  }

  span.sep {
    color: ${C.border};
  }
  span.current {
    color: ${C.text};
  }
`;

/* ── Main 2-col layout ──────────────────────────────────────── */
const ProductLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  padding: 36px 0 80px;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

/* ── Image side ─────────────────────────────────────────────── */
const ImageCol = styled(motion.div)`
  /* position: sticky; */
  top: 32px;
`;

const MainImage = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 24px;
  overflow: hidden;
  background: ${C.white};
  box-shadow: 0 2px 24px rgba(0, 0, 0, 0.07);
  border: 1.5px solid ${C.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover img {
    transform: scale(1.04);
  }
`;

/* ── Info side ──────────────────────────────────────────────── */
const InfoCol = styled(motion.div)`
  padding-top: 8px;
`;

const BrandLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${C.blueSoft};
  border-radius: 100px;
  padding: 6px 14px;
  margin-bottom: 20px;

  span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #578bda;
  }
`;

const ProductName = styled.h1`
  font-family: "Syne", sans-serif;
  font-size: clamp(28px, 3.5vw, 42px);
  font-weight: 800;
  color: #555353;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-bottom: 24px;
  text-transform: capitalize;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 36px;
`;

const PriceAmount = styled.span`
  font-family: "Syne", sans-serif;
  font-size: 38px;
  font-weight: 800;
  color: #578bda;
  letter-spacing: -0.04em;
  line-height: 1;
`;

const PriceCurrency = styled.span`
  font-family: "Syne", sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #578bda;
  opacity: 0.6;
  align-self: flex-start;
  margin-top: 4px;
`;

/* ── Divider ────────────────────────────────────────────────── */
const Divider = styled.div`
  height: 1.5px;
  background: ${C.border};
  margin: 32px 0;
  border-radius: 2px;
`;

/* ── Quantity ───────────────────────────────────────────────── */
const QuantityLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${C.sub};
  margin-bottom: 12px;
`;

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  background: ${C.white};
  border: 1.5px solid ${C.border};
  border-radius: 14px;
  width: fit-content;
  overflow: hidden;
  margin-bottom: 28px;
`;

const QtyBtn = styled.button`
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  font-size: 18px;
  font-weight: 500;
  color: ${C.sub};
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${C.blueSoft};
    color: ${C.blue};
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const QtyValue = styled.span`
  min-width: 44px;
  text-align: center;
  font-family: "Syne", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${C.text};
  border-left: 1.5px solid ${C.border};
  border-right: 1.5px solid ${C.border};
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ── CTA buttons ────────────────────────────────────────────── */
const BuyBtn = styled(motion.button)`
  width: 100%;
  padding: 16px;
  background: #578bda;
  border: none;
  border-radius: 14px;
  font-family: "Figtree", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition:
    background 0.18s,
    box-shadow 0.18s;
  margin-bottom: 12px;

  &:hover {
    background: ${C.blueDeep};
    box-shadow: 0 8px 24px #1a56ff30;
  }
`;

const TotalLine = styled.p`
  text-align: center;
  font-size: 13px;
  color: ${C.sub};
  font-weight: 500;

  strong {
    color: ${C.text};
    font-weight: 700;
  }
`;

/* ── Skeleton: product ──────────────────────────────────────── */
const SkeletonImg = styled.div`
  width: 100%;
  aspect-ratio: 1/1;
  border-radius: 24px;
  ${skeletonAnim}
`;

const SkeletonLine = styled.div`
  height: ${({ h }) => h || 16}px;
  width: ${({ w }) => w || "100%"};
  border-radius: 8px;
  margin-bottom: ${({ mb }) => mb || 12}px;
  ${skeletonAnim}
`;

/* ── Related products ───────────────────────────────────────── */
const RelatedSection = styled.section`
  padding: 0 0 96px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 28px;
  padding-bottom: 18px;
  border-bottom: 1.5px solid ${C.border};
`;

const SectionTitle = styled.h2`
  font-family: "Syne", sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #585858;
  letter-spacing: -0.02em;
`;

const SectionSub = styled.span`
  font-size: 13px;
  color: ${C.sub};
  font-weight: 500;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;

  @media (max-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
`;

const RelatedCard = styled(motion.div)`
  background: ${C.white};
  border-radius: 18px;
  overflow: hidden;
  border: 1.5px solid transparent;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  text-decoration: none;
  display: block;
  cursor: pointer;
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    transform 0.2s;

  &:hover {
    border-color: ${C.blue};
    box-shadow: 0 6px 24px #1a56ff10;
    transform: translateY(-2px);
  }
`;

const RelatedImg = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  background: ${C.surface};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: white;
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    display: block;
  }

  ${RelatedCard}:hover & img {
    transform: scale(1.06);
  }
`;

const RelatedBody = styled.div`
  padding: 12px 14px 16px;

  .brand {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${C.blue};
    margin-bottom: 4px;
  }

  .name {
    font-size: 13px;
    font-weight: 600;
    color: gray;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 8px;
  }

  .price {
    font-family: "Syne", sans-serif;
    font-size: 16px;
    font-weight: 800;
    color: #578bda;
    letter-spacing: -0.02em;
  }
`;

/* ── Back button ────────────────────────────────────────────── */
const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Figtree", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${C.sub};
  background: ${C.white};
  border: 1.5px solid ${C.border};
  border-radius: 100px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.18s;
  margin-top: 20px;
  margin-bottom: 4px;

  &:hover {
    border-color: ${C.blue};
    color: ${C.blue};
    background: ${C.blueSoft};
  }
`;

/* ── Not found ──────────────────────────────────────────────── */
const NotFound = styled.div`
  text-align: center;
  padding: 120px 20px;

  h2 {
    font-family: "Syne", sans-serif;
    font-size: 22px;
    font-weight: 800;
    color: ${C.text};
    margin-bottom: 8px;
  }
  p {
    font-size: 14px;
    color: ${C.sub};
    margin-bottom: 24px;
  }
`;

/* ── Component ──────────────────────────────────────────────── */
export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Detajet e produktit - Pult KS";
  }, []);

  /* fetch single product */
  useEffect(() => {
    setLoading(true);
    setQty(1);
    getDoc(doc(db, "products", id)).then((snap) => {
      if (!snap.exists()) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setProduct({ id: snap.id, ...snap.data() });
      setNotFound(false);
      setLoading(false);
    });
  }, [id]);

  /* fetch related (same brand, exclude current) */
  useEffect(() => {
    if (!product) return;
    const unsub = onSnapshot(collection(db, "products"), (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const rel = all
        .filter((p) => p.id !== id && p.brand === product.brand)
        .slice(0, 8);
      // if not enough from same brand, fill with others
      if (rel.length < 4) {
        const others = all
          .filter((p) => p.id !== id && p.brand !== product.brand)
          .slice(0, 8 - rel.length);
        setRelated([...rel, ...others]);
      } else {
        setRelated(rel);
      }
    });
    return () => unsub();
  }, [product, id]);

  const total = product ? (product.price * qty).toFixed(2) : "0.00";

  if (notFound)
    return (
      <>
        <GlobalStyle />
        <Page>
          <Wrap>
            <NotFound>
              <h2>Product not found</h2>
              <p>This product may have been removed or doesn't exist.</p>
              <BuyBtn
                onClick={() => navigate("/products")}
                style={{
                  margin: "0 auto",
                  width: "auto",
                  padding: "14px 28px",
                }}
              >
                ← Back to Products
              </BuyBtn>
            </NotFound>
          </Wrap>
        </Page>
      </>
    );

  return (
    <>
      <GlobalStyle />
      <Page>
        <Wrap>
          {/* Breadcrumb */}
          <Breadcrumb>
            <Link to="/">Home</Link>
            <span className="sep">/</span>
            <Link to="/products">Produktet</Link>
            <span className="sep">/</span>
            <span className="current">
              {loading ? "Loading…" : product?.name}
            </span>
          </Breadcrumb>

          <BackBtn onClick={() => navigate("/products")}>← Kthehu</BackBtn>

          {/* ── Product layout ── */}
          <ProductLayout>
            {/* Image */}
            <ImageCol
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              {loading ? (
                <SkeletonImg />
              ) : (
                <MainImage>
                  <img src={product.imageUrl} alt={product.name} />
                </MainImage>
              )}
            </ImageCol>

            {/* Info */}
            <InfoCol
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.08,
              }}
            >
              {loading ? (
                <>
                  <SkeletonLine h={32} w="40%" mb={20} />
                  <SkeletonLine h={52} w="90%" mb={10} />
                  <SkeletonLine h={52} w="70%" mb={28} />
                  <SkeletonLine h={44} w="30%" mb={40} />
                  <SkeletonLine h={44} mb={12} />
                  <SkeletonLine h={44} />
                </>
              ) : (
                <>
                  <BrandLine>
                    <span>{product.brand}</span>
                  </BrandLine>

                  <ProductName>{product.name}</ProductName>

                  <PriceRow>
                    <PriceCurrency>€</PriceCurrency>
                    <PriceAmount>
                      {Number(product.price).toFixed(2)}
                    </PriceAmount>
                  </PriceRow>

                  <Divider />

                  {/* Quantity */}
                  <QuantityLabel>Sasia</QuantityLabel>
                  <QuantityRow>
                    <QtyBtn
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      disabled={qty <= 1}
                    >
                      −
                    </QtyBtn>
                    <AnimatePresence mode="popLayout">
                      <QtyValue
                        key={qty}
                        as={motion.span}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.15 }}
                      >
                        {qty}
                      </QtyValue>
                    </AnimatePresence>
                    <QtyBtn onClick={() => setQty((q) => q + 1)}>+</QtyBtn>
                  </QuantityRow>

                  <Divider />

                  {/* CTA */}
                  <BuyBtn
                    whileTap={{ scale: 0.98 }}
                    onClick={() =>
                      navigate("/checkout", { state: { product, qty } })
                    }
                  >
                    Porosit tani
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </BuyBtn>

                  <TotalLine>
                    Totali: <strong>€{total}</strong> per {qty}{" "}
                    {qty === 1 ? "copë" : "copa"}
                  </TotalLine>
                </>
              )}
            </InfoCol>
          </ProductLayout>

          {/* ── Related products ── */}
          {related.length > 0 && (
            <RelatedSection>
              <SectionHeader>
                <SectionTitle>Të ngashme</SectionTitle>
                <SectionSub>{related.length} produkte</SectionSub>
              </SectionHeader>

              <RelatedGrid>
                {related.map((p, i) => (
                  <RelatedCard
                    key={p.id}
                    as={Link}
                    to={`/products/${p.id}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.28, delay: i * 0.05 }}
                  >
                    <RelatedImg>
                      <img src={p.imageUrl} alt={p.name} loading="lazy" />
                    </RelatedImg>
                    <RelatedBody>
                      <div className="brand">{p.brand}</div>
                      <div className="name">{p.name}</div>
                      <div className="price">{Number(p.price).toFixed(2)}€</div>
                    </RelatedBody>
                  </RelatedCard>
                ))}
              </RelatedGrid>
            </RelatedSection>
          )}
        </Wrap>
      </Page>
    </>
  );
}
